import { useMemo, useState, useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
//import { useAuth } from '@clerk/react'
import {
  Separator,
  Spinner,
  YStack,
  useToastController,
  DataLoadingDialog,
  ProgressStepper,
  SimpleText,
  useMedia,
  XStack,
  Accordion,
  Square,
} from '@my/ui'
import { color, useAuthedRequestFn, useLink, useAuth, MiscUtils } from '@my/util'
import assets from '../../../assets/images'
import type { ProgressStepperProps } from '@my/ui'
import { FinancialProfileForm, FinancialProfileTableOfContents } from '../../financial-profile/form'
import { HomeMainHeading } from '../../../components/index'
import { stepLabels } from './../../financial-profile/data'
import { SidebarStateContext } from '../../../provider'
import { keyBy, mapValues } from 'lodash'
import { ChevronDown } from '@tamagui/lucide-icons-2'

export interface ScenarioProfileData {
    profile?: any;
    profileDelta?: { [key: string]: { oldValue: any; newValue: any }};
}

export const ScenarioFinancialProfileScreen: React.FC<{
  scenarioProfileData: ScenarioProfileData;
  setScenarioProfileData: (data: ScenarioProfileData) => void;
}> = ({scenarioProfileData, setScenarioProfileData}) => {
  const { userId } = useAuth()
  const sidebarState = useContext(SidebarStateContext)
  const toast = useToastController()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()
  const { sm } = useMedia()

  const [currentStep, setCurrentStep] = useState(0)
  const [formSubmitting, setFormSubmitting] = useState(false)

  const profileChangesList = Object.entries(scenarioProfileData.profileDelta || {})
  .map(([key, value]) => ({ label: key, ...(value || {}) }));

  const progressValue = useMemo(
    () => Math.round((currentStep / stepLabels.length) * 100),
    [currentStep, stepLabels]
  )

  const formDataQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  })

  const refreshFinancialInstitution = async (institution_obj: any) => {
    const { institution_id } = institution_obj.institution
    const response = await authedRequestFn('/plaid/account_data', {
      query: { userId, institution_id },
    })
    return response
  }

  const handleStepChange = (stepNumber: number) => {
    setCurrentStep(stepNumber)
  }

  const formData = scenarioProfileData.profile || formDataQuery.data;
  const showForm = scenarioProfileData.profile || !formDataQuery.isLoading && formDataQuery.data;


  const onSubmit = (data: any, dirtyFields) => {
    const newScenarioProfileData = {
      profile: data,
      profileDelta: mapValues(dirtyFields, (value, key) => ({oldValue: formDataQuery.data[key], newValue: data[key]}))
    };
    //console.log('yo!', newScenarioProfileData.profileDelta)
    setScenarioProfileData(newScenarioProfileData)
  }
  const onSave = onSubmit//(data: any, dirtyFields) => console.log('huh?', dirtyFields)
  const onDiscard = async (data: any) => (await MiscUtils.dialog.confirm("Are you sure you want to discard these changes?")) ? setScenarioProfileData({}) : void 0;

  const [buttonFns, setButtonFns] = useState<ProgressStepperProps['buttonFns']>({
    discard: {
      disabled: true,
      fn: () => Promise.resolve()
    },
    save: {
      disabled: true,
      fn: () => Promise.resolve()
    }
  });

  const stepsToSkip = [4,8];

  const form = currentStep === 0 ? <FinancialProfileTableOfContents 
    handleStepChange={handleStepChange}
    stepLabels={stepLabels}
    stepsToSkip={stepsToSkip}
    isScenarioPlanning={true}
  /> : <>
    <ProgressStepper
      currentStep={currentStep}
      handleStepChange={handleStepChange}
      stepLabels={stepLabels.filter((_, idx) => !(stepsToSkip || []).includes(idx+1))}
      progressValue={progressValue}
      sidebarState={sidebarState}
      buttonFns={buttonFns}
    />
    {showForm ? (
      <FinancialProfileForm
        data={formData}
        onSubmit={onSubmit}
        refreshFinancialInstitution={refreshFinancialInstitution}
        onDiscard={onDiscard}
        onSave={onSave}
        currentStep={currentStep}
        handleStepChange={handleStepChange}
        stepLabels={stepLabels}
        reportButtonFns={setButtonFns}
        buttonFnsToSkip={['submit']}
        stepsToSkip={stepsToSkip}
      />
    ) : (
      <Spinner size="large" color="$green10" />
    )}
  </>;

  const profileChanges = profileChangesList.length ? <Accordion overflow="hidden" type="multiple" defaultValue={['a1']}>
    <Accordion.Item value="a1">
      <Accordion.Trigger flexDirection="row" justifyContent="space-between">
        {({ open }: { open: boolean }) => (
          <>
            {/* <Paragraph>1. Define your scenario goal</Paragraph> */}
            <SimpleText fontSize={16}>
              Changes you've made to your Financial Profile
            </SimpleText>
            <Square transition="quick" rotate={open ? '180deg' : '0deg'}>
              <ChevronDown size="$1" />
            </Square>
          </>
        )}
      </Accordion.Trigger>
      <Accordion.HeightAnimator transition="medium">
        <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }}>
          <YStack>
            {profileChangesList.map((item, idx) => <ProfileChangesListItem key={idx} {...item} />)}
          </YStack>
        </Accordion.Content>
      </Accordion.HeightAnimator>
    </Accordion.Item>
  </Accordion> : null;

  return (
    <YStack bg="#fff">
      <DataLoadingDialog open={formSubmitting} onOpenChange={setFormSubmitting} />
      <YStack width={'100%'} marginBottom={20} p={15}>
        <SimpleText fontSize={18} fontWeight={'700'}>
          Let's create a custom financial profile for this scenario
        </SimpleText>
        {profileChanges}
      </YStack>
      {form}
      <Separator />
    </YStack>
  )
}

const SmallText = (text: string) => <SimpleText fontSize={14} fontWeight={'400'} color={color.lightGrey}>{text}</SimpleText>;

export const ProfileChangesListItem: React.FC<{
  label: string;
  oldValue: string;
  newValue: string;
  trend?: 'gain' | 'loss'
  key?: any
}> = ({label, oldValue, newValue, trend, key}) => {
  const arrowColor = trend
      ? trend == 'gain'
          ? color.parrotGreen
          : color.lightRed
      : color.lightGrey
  return <YStack key={key}><XStack justifyContent="space-between" alignItems="center" marginBottom="$2">
    <SimpleText fontSize={14} fontWeight={'400'} color={color.grey}>
      {label}
    </SimpleText>
    <SimpleText color={arrowColor} fontSize={16} fontWeight={'700'}>
      {trend == 'gain' ? `↑` : trend == 'loss' ? `↓` : null}
      <SimpleText fontSize={14} fontWeight={'400'} color={color.grey}>
        {SmallText('from')} {String(oldValue)} {SmallText('to')} {String(newValue)}
      </SimpleText>
    </SimpleText>
  </XStack></YStack>
}